import { twMerge } from "tailwind-merge";
import {
  ComponentProps,
  HTMLAttributes,
  ReactElement,
  cloneElement,
} from "react";
import Tooltip from "../tooltip/Tooltip";
import { ICON_COLOR, ICON_SIZE, ICON_VARIANT } from "./types";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import { iconButton, iconSize } from "./constants";

export type Props = ComponentProps<"button"> & {
  icon?: React.ReactElement;
  iconNode?: React.ReactNode;
  children?: React.ReactNode;
  variant?: ICON_VARIANT;
  color?: ICON_COLOR;
  size?: ICON_SIZE;
  message?: string;
  iconClassName?: string;
  hideTooltip?: boolean;
  "aria-label": string;
};

export const IconButton = ({
  className,
  variant = ICON_VARIANT.DEFAULT,
  icon,
  iconNode,
  size = ICON_SIZE.LARGE,
  color = ICON_COLOR.DEFAULT,
  message,
  iconClassName,
  hideTooltip,
  onClick,
  type,
  "aria-label": ariaLabelAttr,
  ...props
}: Props) => {
  const isMobile = useIsMobile();
  const mode = iconButton[variant][color];
  const accessibleName = ariaLabelAttr || message;
  const showTooltip = Boolean(!hideTooltip && message && !isMobile);

  return (
    <button
      {...props}
      onClick={onClick}
      className={twMerge(
        "flex items-center justify-center rounded-6",
        color,
        iconSize[size].size,
        mode.bg,
        mode.border,
        className
      )}
      aria-label={accessibleName}
      type={type || "button"}
    >
      {showTooltip ? (
        <Tooltip
          className={iconSize[size].tooltip}
          message={message}
          showArrow
          anchor={typeof window === "undefined" ? undefined : document.body}
        >
          <div>
            {icon &&
              cloneElement(icon as ReactElement<HTMLAttributes<HTMLElement>>, {
                className: twMerge(
                  iconSize[size].icon,
                  mode.fill,
                  iconClassName
                ),
              })}
          </div>
        </Tooltip>
      ) : (
        iconNode ||
        (icon &&
          cloneElement(icon as ReactElement<HTMLAttributes<HTMLElement>>, {
            className: twMerge(iconSize[size].icon, iconClassName),
          }))
      )}
    </button>
  );
};
